import {Injectable} from '@angular/core';
import {HttpClient, HttpErrorResponse, HttpHeaders} from '@angular/common/http';
import {Observable, Subject, throwError} from 'rxjs';
import {catchError} from 'rxjs/operators';
import {environment} from '../../../environments/environment';
import {AlertService} from '../notify/alert.service';
import {Router} from '@angular/router';

class ApiResponse {
    response: ApiResponseMessage | any;
    code: string;
}

export class ApiResponseMessage {
    message: string;
}

@Injectable({
    providedIn: 'root',
})
export class ConnectorService {

    apiURL = (window['_env_']['apiURL'] || environment.url) + '/api/v1/';

    constructor(
        protected http: HttpClient,
        protected alert: AlertService,
        protected router: Router,
    ) {
    }

    /**
     * GET запрос
     *
     * @param url
     * @param data
     * @param domain // междоменные контракты. запрос на домен таргет данных
     */
    get(url: string, data: any = null,domain?:string): Observable<any> {
        return new Observable(subscriber => {
            this.http.get((domain||this.apiURL) + url, this.headers({params: data}))
                .pipe(catchError(this.handleError.bind(this, subscriber)))
                .subscribe((result: ApiResponse) => {
                    subscriber.next(result.response);
                    subscriber.complete();
                });
        });
    }

    /**
     * POST запрос
     *
     * @param url
     * @param data
     * @param convertToForm - переводить тело в формат формы
     */
    post(url: string, data: any = null, convertToForm = true): Observable<any> {
        return new Observable(subscriber => {
            let form = new FormData();
            if (convertToForm) {
                this.parseArrayToFormData(form, data);
            } else {
                form = data;
            }
            this.http.post(this.apiURL + url, form, this.headers())
                .pipe(catchError(this.handleError.bind(this, subscriber)))
                .subscribe((result: ApiResponse) => {
                    subscriber.next(result?.response);
                    subscriber.complete();
                });
        });
    }

    /**
     * PUT запрос
     * @param url
     * @param data
     * @param convertToForm - переводить тело в формат формы
     */
    put(url, data: any = null, convertToForm = true): Observable<any> {
        return new Observable(subscriber => {
            let form = new FormData();
            if (convertToForm) {
                this.parseArrayToFormData(form, data);
            } else {
                form = data;
            }
            this.http.put(this.apiURL + url, form, this.headers())
                .pipe(catchError(this.handleError.bind(this, subscriber)))
                .subscribe((result: ApiResponse) => {
                    subscriber.next(result.response);
                    subscriber.complete();
                });
        });
    }

    /**
     * DELETE запрос
     * @param url
     * @param data
     * @param convertToForm
     * @protected
     */
    delete(url, data: any = null, convertToForm = true): Observable<any> {
        return new Observable(subscriber => {
            let form = new FormData();
            if (convertToForm) {
                this.parseArrayToFormData(form, data);
            } else {
                form = data;
            }

            this.http.delete(this.apiURL + url, this.headers({body: form}))
                .pipe(catchError(this.handleError.bind(this, subscriber)))
                .subscribe((result: ApiResponse) => {
                    subscriber.next(result.response);
                    subscriber.complete();
                });
        });
    }

    protected headers(header: any = {}): object {
        const token = localStorage.getItem('access_token');
        if (token) {
            header.headers = new HttpHeaders({
                Authorization: token,
            });
        }
        return header;
    }

    /**
     * Загрузка тела документа
     *
     * @param url
     * @param data
     * @protected
     */
    protected getBodyFile(url, data: any = null): Observable<Blob> {
        return new Observable(subscriber => {
            this.http.get(this.apiURL + url, this.headers({responseType: 'blob', params: data}))
                .pipe(catchError(this.handleError.bind(this, subscriber)))
                .subscribe((result: Blob) => {
                    subscriber.next(result);
                    subscriber.complete();
                });
        });
    }

    protected handleError(obs$: Subject<ApiResponse>, error: HttpErrorResponse): void {
        console.log('nadleErr',error.status)
        let response = error.status === 304? {message:'Data have not been modified'} : error.error?.response
        obs$.error(response);

        switch (error.status) {
            case 304: {
                /* Data already exists */
                console.log('304',error)
                throw throwError({message:'304 - Not Modified'});
            }
            case 400: {
                /* BAD_REQUEST */
                throw throwError(error.error.response);
            }
            case 401: {
                /* UNAUTHORIZED */
                this.alert.setAlert(error.error.response.message, 'blue');
                const token = localStorage.getItem('access_token');
                if (token) {
                    localStorage.removeItem('access_token');
                    localStorage.removeItem('expires_in');
                    sessionStorage.clear();
                }
                void this.router.navigate(['/login'], {queryParams: this.router.parseUrl(this.router.url).queryParams});

                throw throwError(error.error.response);
            }
            case 403: {
                /* FORBIDDEN */

                // TODO Понять, насколько это правильно относительно работы системы

                // localStorage.removeItem('access_token');
                // localStorage.removeItem('expires_in');
                // sessionStorage.clear();
                // void this.router.navigate(['/login'], {queryParams: this.router.parseUrl(this.router.url).queryParams});
                this.alert.setAlert(error.error.response.message, 'blue');
                throw throwError(error.error.response);
            }
            case 404: {
                /* NOT FOUND */
                this.alert.setAlert(error.error.response?.message, 'blue');
                throw throwError(error.error.response);
            }
            case 405: {
                /* MethodNotAllowed */
                this.alert.setAlert(error.error.response.message, 'blue');
                throw throwError(error.error.response);
            }
            case 0: {
                /* ERR_CONNECTION_REFUSED */
                this.alert.setAlert('INVALID CONNECT TO SERVER', 'blue');
                throw throwError('INVALID CONNECT TO SERVER');
            }
            default: {
                throw throwError(error);
            }
        }
    }

    /**
     * конвертация даты в форму (с поддержкой многомерных массивов)
     *
     * @param formData
     * @param values
     * @param name
     * @protected
     */
    protected parseArrayToFormData(formData: FormData, values, name = null): void {
        if (!values && name) {
            formData.append(name, '');
        } else {
            if (typeof values === 'object') {
                for (const key in values) {
                    const nName = (name) ? name + '[' + key + ']' : key;
                    if (values.hasOwnProperty(key) && typeof values[key] === 'object') {
                        this.parseArrayToFormData(formData, values[key], nName);
                    } else {
                        formData.append(nName, values[key]);
                    }
                }
            } else {
                formData.append(name, values);
            }
        }
    }
}
